import { cva } from 'class-variance-authority';

/**
 * Spinner class recipes. Colour lives on `.ds-spinner` in `design-system.css`, so there is no `dark:`
 * variant here.
 *
 * Ported from Figma `Spinner`, page `Spinner [Draft]`, read 2026-09-29: Size (xs 16 / sm 20 / md 24 /
 * lg 32 / xl 40), a `showLabel` boolean and a label text. Label styles per size are the file's:
 * xs and sm `Body/sm`, md and lg `Body/md`, xl `Body/lg`. The gap between glyph and label is x2 (8).
 */
export const spinnerRecipe = cva(
  [
    'ds-spinner inline-flex items-center gap-2 text-[var(--ds-spinner-fg)]',
    // Arrives on the control clock, and is UNMOUNTED rather than faded out, for the reason
    // `buttonSpinnerRecipe` gives: a spinning glyph must not outlive the work. The fade is kept
    // under `reduce` (opacity is meaning, not movement; see `docs/motion.md`), so there is no
    // `motion-reduce` escape here.
    'transition-[opacity,visibility] duration-[var(--motion-control)] ease-[var(--motion-control-ease)]',
    'starting:opacity-0',
  ],
  {
    variants: {
      /**
       * Held back for `--motion-delay`, so a load that finishes first shows nothing. It is HIDDEN, not
       * removed: `visibility` keeps the box, so nothing shifts when it appears, and it takes the
       * status out of the accessibility tree until then, so it is not announced for a load nobody
       * saw. `starting:invisible` is the value a newly mounted element transitions FROM, and the
       * transition delay holds it there.
       */
      delayed: { true: 'starting:invisible delay-[var(--motion-delay)]', false: '' },
    },
    defaultVariants: { delayed: false },
  }
);

/** The box the ring is drawn into: 16 / 20 / 24 / 32 / 40, the icon sizes plus MUI's 40 default. */
export const spinnerGlyphBoxRecipe = cva('inline-flex shrink-0', {
  variants: {
    size: { xs: 'size-4', sm: 'size-5', md: 'size-6', lg: 'size-8', xl: 'size-10' },
  },
  defaultVariants: { size: 'md' },
});

/**
 * The ring itself, shared by every host.
 *
 * Rotation is `--motion-spin`. Under reduced motion it stops at the drawn frame (the notch still
 * reads as "loading", where an empty ring would not) and the glyph pulses its opacity instead: no
 * movement, and it works in an icon-only button or a tag, which have no room for a label. Both
 * classes are plain, unqualified declarations on one element, so the media-query variant wins by
 * source order; see the specificity note in `docs/motion.md`.
 */
export const spinnerGlyphRecipe = cva(['block size-full origin-center animate-ds-spin motion-reduce:animate-ds-pulse']);

/**
 * The label. Always in the DOM so the status has a name; visible only when the caller asks. Wraps to
 * the width its container allows rather than overflowing.
 */
export const spinnerLabelRecipe = cva('min-w-0 break-words', {
  variants: {
    size: { xs: 'text-body-sm', sm: 'text-body-sm', md: 'text-body-md', lg: 'text-body-md', xl: 'text-body-lg' },
    visible: { true: '', false: 'sr-only' },
  },
  defaultVariants: { size: 'md', visible: false },
});
